import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { apiService } from '../services/api';
import { ScrollLink } from './ScrollLink';
import '../styles/Header.css';

function  Header() {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasResumeAccess, setHasResumeAccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const userData = currentUser?.user || currentUser;
  const currentUserId = userData?.id;
  const isAdmin = Boolean(userData?.is_admin);
  const canUseResume = isAdmin || hasResumeAccess;

  useEffect(() => {
    const syncCurrentUser = () => {
      setCurrentUser(authService.getCurrentUser());
    };

    syncCurrentUser();
    window.addEventListener('authChanged', syncCurrentUser);

    return () => window.removeEventListener('authChanged', syncCurrentUser);
  }, []);

  useEffect(() => {
    let active = true;
    if (!currentUserId) return undefined;

    apiService.user.get()
      .then((response) => {
        if (active) authService.updateStoredUser(response.data);
      })
      .catch(() => {
        // A sessão existente continua válida; o interceptor cuida de tokens expirados.
      });

    return () => {
      active = false;
    };
  }, [currentUserId]);

  useEffect(() => {
    let active = true;
    if (!currentUser) {
      setHasResumeAccess(false);
      return undefined;
    }
    if (isAdmin) {
      setHasResumeAccess(true);
      return undefined;
    }

    apiService.resumes.access()
      .then((response) => {
        if (active) setHasResumeAccess(Boolean(response.data?.has_access));
      })
      .catch(() => {
        if (active) setHasResumeAccess(false);
      });

    return () => {
      active = false;
    };
  }, [currentUser, isAdmin, location.pathname]);

  useEffect(() => {
    setShowMobileMenu(false);
    setShowUserMenu(false);
  }, [location]);

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleMobileMenuToggle = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const handleLogout = async () => {
    await authService.logout();
    setCurrentUser(null);
    navigate('/login');
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          {/* Logo */}
          <ScrollLink to="/" className="logo">
            <img src="/logo-abraliber.png" alt="ABRALIBER Educação Online" className="logo-img" />
          </ScrollLink>

          {/* Desktop Navigation */}
          <nav className="nav-desktop">
            <ScrollLink to="/sobre-a-abraliber" className="nav-link">
              <span>Sobre</span>
            </ScrollLink>

            <ScrollLink to="/cursos" className="nav-link">
              <span>Cursos</span>
            </ScrollLink>
            
            <ScrollLink to="/certificados" className="nav-link">
              <span>Certificados</span>
            </ScrollLink>
            
            <ScrollLink to="/contato" className="nav-link">
              <span>Contato</span>
            </ScrollLink>
          </nav>

          {/* Auth Section */}
          <div className="auth-section">
            {currentUser ? (
              <div className="user-menu">
                <button 
                  className="user-menu-trigger"
                  onClick={handleUserMenuToggle}
                  aria-expanded={showUserMenu}
                >
                  <div className="user-avatar">
                    <span>{currentUser.user?.name?.charAt(0)?.toUpperCase() || currentUser.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                  </div>
                  <span className="user-name">{currentUser.user?.name || currentUser.name || 'Usuário'}</span>
                  <svg className={`dropdown-icon ${showUserMenu ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                {showUserMenu && (
                  <div className="user-dropdown">
                    {isAdmin && (
                      <>
                        <ScrollLink to="/admin" className="user-dropdown-item admin-menu-entry">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 5H20V19H4V5Z" stroke="currentColor" strokeWidth="2" />
                            <path d="M8 9H16M8 13H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                          <span>Painel Administrativo</span>
                        </ScrollLink>
                        <div className="dropdown-divider"></div>
                      </>
                    )}
                    <ScrollLink to="/meus-cursos" className="user-dropdown-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 14L8 10L16 10L12 14Z" fill="currentColor"/>
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Meus Cursos</span>
                    </ScrollLink>
                    <ScrollLink to="/meus-certificados" className="user-dropdown-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 7V4C4 3.44772 4.44772 3 5 3H19C19.5523 3 20 3.44772 20 4V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 11L9 14L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Certificados</span>
                    </ScrollLink>
                    <ScrollLink to="/meus-dados" className="user-dropdown-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <span>Meus Dados</span>
                    </ScrollLink>
                    {canUseResume && (
                      <ScrollLink to="/curriculo" className="user-dropdown-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 2H14L19 7V22H6V2Z" stroke="currentColor" strokeWidth="2"/>
                          <path d="M14 2V7H19M9 12H16M9 16H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span>Meu Currículo</span>
                      </ScrollLink>
                    )}
                    <div className="dropdown-divider"></div>
                    <button onClick={handleLogout} className="user-dropdown-item logout-btn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Sair</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-buttons">
                <ScrollLink to="/login" className="btn btn-outline btn-sm">
                  Entrar
                </ScrollLink>
                <ScrollLink to="/cadastro" className="btn btn-primary btn-sm">
                  Cadastrar
                </ScrollLink>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={handleMobileMenuToggle}
            aria-label="Menu"
          >
            <span className={`hamburger ${showMobileMenu ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>

        {/* Mobile Navigation */}
        {showMobileMenu && (
          <div className="mobile-nav">
            <ScrollLink to="/" className="mobile-nav-link">Home</ScrollLink>
            <ScrollLink to="/sobre-a-abraliber" className="mobile-nav-link">Sobre</ScrollLink>
            <ScrollLink to="/cursos" className="mobile-nav-link">Cursos</ScrollLink>
            <ScrollLink to="/certificados" className="mobile-nav-link">Certificados</ScrollLink>
            <ScrollLink to="/contato" className="mobile-nav-link">Contato</ScrollLink>
            
            {currentUser ? (
              <>
                <div className="mobile-nav-divider"></div>
                {isAdmin && <ScrollLink to="/admin" className="mobile-nav-link admin-menu-entry">Painel Administrativo</ScrollLink>}
                <ScrollLink to="/meus-cursos" className="mobile-nav-link">Meus Cursos</ScrollLink>
                <ScrollLink to="/meus-certificados" className="mobile-nav-link">Meus Certificados</ScrollLink>
                <ScrollLink to="/meus-dados" className="mobile-nav-link">Meus Dados</ScrollLink>
                {canUseResume && <ScrollLink to="/curriculo" className="mobile-nav-link">Meu Currículo</ScrollLink>}
                <button onClick={handleLogout} className="mobile-nav-link logout-mobile">
                  Sair
                </button>
              </>
            ) : (
              <>
                <div className="mobile-nav-divider"></div>
                <ScrollLink to="/login" className="mobile-nav-link">Entrar</ScrollLink>
                <ScrollLink to="/cadastro" className="mobile-nav-link primary">Cadastrar</ScrollLink>
              </>
            )}
          </div>
        )}
      </header>
    </>
  );
}

export default Header;
